import { motion } from 'framer-motion';
import useGameStore from '@/store/gameStore';

function LobbyPhase() {
  const { gameState } = useGameStore();

  if (!gameState) return null;

  const humanPlayerCount = Object.values(gameState.players).filter((p) => !p.isAi).length;
  const aiPlayerCount = gameState.config.aiPlayerCount;
  const totalPlayers = humanPlayerCount + aiPlayerCount;
  const minPlayers = 3;
  const needMore = Math.max(0, minPlayers - totalPlayers);

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        className="text-center"
      >
        <div className="text-6xl mb-4">🎮</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Waiting for Players</h2>

        <div className="mb-6">
          <p className="text-4xl font-mono font-bold text-primary-600 mb-2">{gameState.roomCode}</p>
          <p className="text-sm text-gray-500">Share this code with friends!</p>
        </div>

        <div className="mb-6">
          <p className="text-lg text-gray-600">
            {totalPlayers} / {minPlayers} players{' '}
            {aiPlayerCount > 0 && (
              <span className="text-sm text-gray-500">
                ({humanPlayerCount} human + {aiPlayerCount} AI)
              </span>
            )}
          </p>
          {needMore > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Need {needMore} more player{needMore !== 1 ? 's' : ''} to start
            </p>
          )}
        </div>

        {/* Player Avatars */}
        <div className="flex justify-center gap-2 mb-4">
          {Object.values(gameState.players).map((player, index) => (
            <motion.div
              key={player.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="w-16 h-16 rounded-full flex items-center justify-center bg-primary-400 overflow-hidden"
              title={player.name}
            >
              {player.avatar.startsWith('http') ? (
                <img src={player.avatar} alt={player.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl">{player.avatar}</span>
              )}
            </motion.div>
          ))}
          {/* Show AI player placeholders */}
          {Array.from({ length: aiPlayerCount }, (_, i) => (
            <div
              key={`ai-${i}`}
              className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-300 text-2xl"
              title={`AI Player ${i + 1}`}
            >
              🤖
            </div>
          ))}
          {/* Show empty slots */}
          {Array.from({ length: needMore }, (_, i) => (
            <div
              key={`empty-${i}`}
              className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-200 border-2 border-dashed border-gray-400 text-2xl"
            >
              ?
            </div>
          ))}
        </div>

        {/* Waiting Animation */}
        <div className="flex justify-center gap-1">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 bg-primary-400 rounded-full"
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default LobbyPhase;
